import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, OnInit, inject } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Attachment, Coding, Organization } from 'fhir/r4b';
import { take } from 'rxjs';
import { backendEndPointToken, terminologyCanonicalUrlBaseToken } from '../app.config';
import { HospitalService } from '../hospital-details/hospital.service';
import { FormFieldsSelectDataService } from '../shared/form-fields-select-data.service';
import { AuthService } from '../shared/auth/auth.service';
import { ErrorService } from '../shared/error.service';
import { UploadUiComponent } from '../upload-ui/upload-ui.component';

@Component({
  selector: 'app-company-settings',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    MatProgressBarModule,
    MatSnackBarModule,
    UploadUiComponent,
  ],
  templateUrl: './company-settings.component.html',
  styleUrl: './company-settings.component.scss'
})
export class CompanySettingsComponent implements OnInit {
  private fb = inject(FormBuilder);
  private http = inject(HttpClient);
  private backendUrl = inject(backendEndPointToken);
  private terminologyBaseUrl = inject(terminologyCanonicalUrlBaseToken);
  private auth = inject(AuthService);
  private selectData = inject(FormFieldsSelectDataService);
  private snackBar = inject(MatSnackBar);
  private errorService = inject(ErrorService);
  private hospitalService = inject(HospitalService);

  form = this.fb.group({
    defaultCurrency: [null as Coding | null, Validators.required],
    lowStock: this.fb.group({
      enabled: [true],
      medicationThreshold: [null as number | null, [Validators.min(0)]],
      supplyThreshold: [null as number | null, [Validators.min(0)]],
      emails: this.fb.array([])
    }),
    customSettings: this.fb.array([]),
    branding: this.fb.group({
      logo: [null as Attachment | null]
    })
  });

  currencyOptions: Coding[] = [];
  loading = true;
  saving = false;
  loadError: string | null = null;
  private organization?: Organization;
  private organizationId: string | null = null;
  private pendingCurrencyFromResource: Coding | null = null;
  logoFiles: any[] = [];
  readonly logoAllowedFilePattern = /\.(png|jpe?g|svg)$/i;
  readonly logoMaxFileSize = 5 * 1024 * 1024;

  private readonly extUrls = {
    defaultCurrency: `${this.terminologyBaseUrl}/StructureDefinition/defaultCurrency`,
    lowStockAlerts: `${this.terminologyBaseUrl}/StructureDefinition/lowStockAlerts`,
    customPreference: `${this.terminologyBaseUrl}/StructureDefinition/customPreference`,
    organizationLogo: `${this.terminologyBaseUrl}/StructureDefinition/organizationLogo`
  } as const;

  private readonly lowStockChildUrls = {
    enabled: 'enabled',
    medicationThreshold: 'medicationThreshold',
    supplyThreshold: 'supplyThreshold',
    notificationEmail: 'notificationEmail'
  } as const;

  private readonly customPreferenceChildUrls = {
    key: 'key',
    value: 'value'
  } as const;

  private readonly managedExtensionUrls = new Set<string>([
    this.extUrls.defaultCurrency,
    this.extUrls.lowStockAlerts,
    this.extUrls.customPreference,
    this.extUrls.organizationLogo
  ]);

  ngOnInit(): void {
    this.addNotificationEmail();
    this.addCustomSetting();
    this.fetchCurrencyOptions();
    this.loadOrganization();
  }

  get lowStockGroup(): FormGroup {
    return this.form.get('lowStock') as FormGroup;
  }

  get lowStockEmails(): FormArray<FormControl<string | null>> {
    return this.lowStockGroup.get('emails') as FormArray<FormControl<string | null>>;
  }

  get customSettings(): FormArray<FormGroup> {
    return this.form.get('customSettings') as FormArray<FormGroup>;
  }

  get brandingGroup(): FormGroup {
    return this.form.get('branding') as FormGroup;
  }

  get logoControl(): FormControl<Attachment | null> {
    return this.brandingGroup.get('logo') as FormControl<Attachment | null>;
  }

  compareCoding = (a: Coding | null, b: Coding | null): boolean => {
    if (!a || !b) {
      return a === b;
    }
    return (a.code || '') === (b.code || '') && (a.system || '') === (b.system || '');
  };

  trackByIndex = (index: number) => index;

  addNotificationEmail(value = ''): void {
    this.lowStockEmails.push(this.fb.control<string | null>(value, [Validators.email]));
  }

  removeNotificationEmail(index: number): void {
    if (this.lowStockEmails.length === 1) {
      this.lowStockEmails.at(0)?.reset('');
      return;
    }
    this.lowStockEmails.removeAt(index);
  }

  addCustomSetting(pref?: { key?: string; value?: string }): void {
    this.customSettings.push(this.fb.group({
      key: [(pref?.key ?? '').trim()],
      value: [(pref?.value ?? '').trim()]
    }));
  }

  removeCustomSetting(index: number): void {
    if (this.customSettings.length === 1) {
      this.customSettings.at(0)?.reset({ key: '', value: '' });
      return;
    }
    this.customSettings.removeAt(index);
  }

  retry(): void {
    this.loadError = null;
    this.loading = true;
    this.loadOrganization();
  }

  save(): void {
    if (!this.organizationId || this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const updated = this.buildUpdatedOrganization();
    this.saving = true;

    this.http
      .put<Organization>(`${this.backendUrl}/Organization/${this.organizationId}`, updated, {
        headers: {
          'Content-Type': 'application/fhir+json',
          Accept: 'application/fhir+json'
        }
      })
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.saving = false;
          this.organization = response;
          this.populateForm(response);
          this.hospitalService.emitOrganizationUpdate(response);
          this.snackBar.open('Company settings updated', 'Close', { duration: 3000 });
        },
        error: (error) => {
          console.error('Failed to update company settings', error);
          this.saving = false;
          this.errorService.openandCloseError('Unable to save settings. Please try again.');
        }
      });
  }

  private fetchCurrencyOptions(): void {
    this.selectData
      .getFormFieldSelectData('chargeItemDef', 'currency')
      .pipe(take(1))
      .subscribe({
        next: (options: Coding[]) => {
          this.currencyOptions = Array.isArray(options) ? options : [];
          this.applyPendingCurrency();
        },
        error: (err) => {
          console.error('Failed to load currency options', err);
          this.currencyOptions = [];
          this.applyPendingCurrency();
        }
      });
  }

  private loadOrganization(): void {
    const orgId = this.auth.user.getValue()?.organizationId;
    if (!orgId) {
      this.loading = false;
      this.loadError = 'No organization is associated with your account.';
      return;
    }

    this.organizationId = orgId;

    this.http
      .get<Organization>(`${this.backendUrl}/Organization/${encodeURIComponent(orgId)}`, {
        headers: { Accept: 'application/fhir+json' }
      })
      .pipe(take(1))
      .subscribe({
        next: (org) => {
          this.organization = org;
          this.populateForm(org);
          this.loading = false;
        },
        error: (error) => {
          console.error('Failed to load organization', error);
          this.loading = false;
          this.loadError = 'Unable to load company settings.';
        }
      });
  }

  private populateForm(org: Organization): void {
    const currency = this.extractCurrency(org);
    this.pendingCurrencyFromResource = currency;
    this.applyPendingCurrency();

    const lowStock = this.extractLowStock(org);
    this.lowStockGroup.patchValue({
      enabled: lowStock?.enabled ?? true,
      medicationThreshold: lowStock?.medicationThreshold ?? null,
      supplyThreshold: lowStock?.supplyThreshold ?? null
    });

    this.lowStockEmails.clear();
    (lowStock?.emails?.length ? lowStock.emails : ['']).forEach((email) => this.addNotificationEmail(email));

    const preferences = this.extractCustomPreferences(org);
    this.customSettings.clear();
    if (preferences.length) {
      preferences.forEach((pref) => this.addCustomSetting(pref));
    } else {
      this.addCustomSetting();
    }

    const logo = this.extractOrganizationLogo(org);
    this.syncLogoState(logo);
  }

  private applyPendingCurrency(): void {
    if (!this.pendingCurrencyFromResource) {
      return;
    }

    const match = this.currencyOptions.find(
      (option) =>
        (option.code || '') === (this.pendingCurrencyFromResource?.code || '') &&
        (option.system || '') === (this.pendingCurrencyFromResource?.system || '')
    );

    const value = match || this.pendingCurrencyFromResource;

    if (value && !match) {
      this.currencyOptions = [value, ...this.currencyOptions];
    }

    this.form.get('defaultCurrency')?.setValue(value);
    this.pendingCurrencyFromResource = null;
  }

  private extractCurrency(org: Organization): Coding | null {
    const ext = org.extension?.find((item) => item.url === this.extUrls.defaultCurrency);
    const coding = ext?.valueCoding;
    if (!coding) {
      return null;
    }
    return {
      code: coding.code,
      display: coding.display,
      system: coding.system
    };
  }

  private extractLowStock(org: Organization): { enabled: boolean; medicationThreshold: number | null; supplyThreshold: number | null; emails: string[] } | null {
    const ext = org.extension?.find((item) => item.url === this.extUrls.lowStockAlerts);
    if (!ext?.extension) {
      return null;
    }

    const findChild = (url: string) => ext.extension?.find((child) => child.url === url);

    const enabled = !!findChild(this.lowStockChildUrls.enabled)?.valueBoolean;
    const medicationThreshold = this.toNumber(findChild(this.lowStockChildUrls.medicationThreshold));
    const supplyThreshold = this.toNumber(findChild(this.lowStockChildUrls.supplyThreshold));
    const emails = ext.extension
      .filter((child) => child.url === this.lowStockChildUrls.notificationEmail)
      .map((child) => (child.valueString ?? '').trim())
      .filter((value) => !!value);

    return { enabled, medicationThreshold, supplyThreshold, emails };
  }

  private extractCustomPreferences(org: Organization): Array<{ key: string; value: string }> {
    const matches = (org.extension ?? []).filter((ext) => ext.url === this.extUrls.customPreference && Array.isArray(ext.extension));
    return matches
      .map((ext) => {
        const key = ext.extension?.find((child) => child.url === this.customPreferenceChildUrls.key)?.valueString?.trim();
        const value = ext.extension?.find((child) => child.url === this.customPreferenceChildUrls.value)?.valueString?.trim();
        if (!key || !value) {
          return null;
        }
        return { key, value };
      })
      .filter((pref): pref is { key: string; value: string } => !!pref);
  }

  private extractOrganizationLogo(org: Organization): Attachment | null {
    const ext = org.extension?.find((item) => item.url === this.extUrls.organizationLogo);
    if (!ext?.valueAttachment) {
      return null;
    }
    return { ...ext.valueAttachment };
  }

  handleLogoFilesChanged(files: any[]): void {
    const normalized = Array.isArray(files) ? files.slice(0, 1) : [];
    this.logoFiles = normalized;
    const attachment = normalized.length ? this.mapUploadFileToAttachment(normalized[0]) : null;
    this.logoControl.setValue(attachment);
  }

  clearLogo(): void {
    this.logoFiles = [];
    this.logoControl.setValue(null);
  }

  private syncLogoState(logo: Attachment | null): void {
    this.logoControl.setValue(logo ?? null, { emitEvent: false });
    this.logoFiles = logo ? [this.mapAttachmentToUploadFile(logo)] : [];
  }

  private buildUpdatedOrganization(): Organization {
    const source = this.organization ?? ({ resourceType: 'Organization', id: this.organizationId! } as Organization);
    const clone = JSON.parse(JSON.stringify(source)) as Organization;
    const remainingExtensions = (clone.extension ?? []).filter((ext) => !this.managedExtensionUrls.has(ext.url || ''));

    const nextExtensions = [...remainingExtensions];

    const currency = this.form.get('defaultCurrency')?.value as Coding | null;
    if (currency) {
      nextExtensions.push({
        url: this.extUrls.defaultCurrency,
        valueCoding: {
          code: currency.code,
          display: currency.display,
          system: currency.system
        }
      });
    }

    const lowStock = this.serializeLowStockExtension();
    if (lowStock) {
      nextExtensions.push(lowStock);
    }

    this.buildCustomPreferenceExtensions().forEach((ext) => nextExtensions.push(ext));

    const logoAttachment = this.logoControl.value;
    if (logoAttachment) {
      nextExtensions.push({
        url: this.extUrls.organizationLogo,
        valueAttachment: this.sanitizeAttachment(logoAttachment)
      });
    }

    clone.extension = nextExtensions.length ? nextExtensions : undefined;
    return clone;
  }

  private serializeLowStockExtension(): any | null {
    const group = this.lowStockGroup.value as {
      enabled: boolean;
      medicationThreshold: number | null;
      supplyThreshold: number | null;
    };

    const emails = this.lowStockEmails.controls
      .map((control) => (control.value || '').trim())
      .filter((value) => !!value);

    const children: any[] = [
      { url: this.lowStockChildUrls.enabled, valueBoolean: !!group.enabled }
    ];

    const medicationThreshold = this.normalizeThreshold(group.medicationThreshold);
    const supplyThreshold = this.normalizeThreshold(group.supplyThreshold);

    if (medicationThreshold !== null) {
      children.push({ url: this.lowStockChildUrls.medicationThreshold, valueInteger: medicationThreshold });
    }

    if (supplyThreshold !== null) {
      children.push({ url: this.lowStockChildUrls.supplyThreshold, valueInteger: supplyThreshold });
    }

    emails.forEach((email) => {
      children.push({ url: this.lowStockChildUrls.notificationEmail, valueString: email });
    });

    return {
      url: this.extUrls.lowStockAlerts,
      extension: children
    };
  }

  private buildCustomPreferenceExtensions(): any[] {
    return this.customSettings.controls
      .map((group) => {
        const key = (group.get('key')?.value || '').toString().trim();
        const value = (group.get('value')?.value || '').toString().trim();
        if (!key || !value) {
          return null;
        }
        return {
          url: this.extUrls.customPreference,
          extension: [
            { url: this.customPreferenceChildUrls.key, valueString: key },
            { url: this.customPreferenceChildUrls.value, valueString: value }
          ]
        };
      })
      .filter((ext): ext is any => !!ext);
  }

  private mapAttachmentToUploadFile(att: Attachment): any {
    return {
      fileLink: att.url,
      uploadFileName: att.title || this.filenameFromUrl(att.url) || 'Logo',
      uploadFileType: att.contentType,
      uploadFileSize: att.size,
      uploadDate: att.creation
    };
  }

  private mapUploadFileToAttachment(file: any): Attachment | null {
    if (!file) {
      return null;
    }

    return {
      title: file.uploadFileName || file.title,
      url: file.fileLink || file.url,
      contentType: file.uploadFileType || file.contentType,
      size: file.uploadFileSize || file.size,
      creation: this.normalizeDateString(file.uploadDate || file.creation)
    };
  }

  private sanitizeAttachment(attachment: Attachment): Attachment {
    const cleaned: Attachment = {};
    if (attachment.contentType) cleaned.contentType = attachment.contentType;
    if (attachment.url) cleaned.url = attachment.url;
    if (attachment.title) cleaned.title = attachment.title;
    if (attachment.creation) cleaned.creation = attachment.creation;
    if (typeof attachment.size === 'number') cleaned.size = attachment.size;
    return cleaned;
  }

  private normalizeDateString(raw: any): string | undefined {
    if (!raw) {
      return undefined;
    }
    if (typeof raw === 'string') {
      return raw;
    }
    try {
      const date = new Date(raw);
      return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
    } catch {
      return undefined;
    }
  }

  private filenameFromUrl(url?: string): string | undefined {
    if (!url) {
      return undefined;
    }
    const parts = url.split('/');
    const lastPart = parts[parts.length - 1];
    if (!lastPart) {
      return undefined;
    }
    return decodeURIComponent(lastPart.split('?')[0]);
  }

  private toNumber(child: any): number | null {
    const raw = child?.valueInteger ?? child?.valueDecimal ?? child?.valueQuantity?.value;
    if (raw === null || raw === undefined) {
      return null;
    }
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private normalizeThreshold(value: number | null | undefined): number | null {
    if (value === null || value === undefined) {
      return null;
    }
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed < 0) {
      return null;
    }
    return Math.floor(parsed);
  }
}
